import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Radio, Space, Table, Tag, message } from 'antd';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiService } from '../services/api';
import {
  MerchantEventDTO,
  MerchantSummaryDTO
} from '../types/auth';
import { isAuthenticated } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Search } = Input;

const OfferMerchants: React.FC = () => {
  const navigate = useNavigate();
  const [exportForm] = Form.useForm();

  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);

  const [merchants, setMerchants] = useState<MerchantSummaryDTO[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantSummaryDTO | null>(null);
  const [events, setEvents] = useState<MerchantEventDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const loadMerchants = useCallback(async (query: string) => {
    setLoadingMerchants(true);
    try {
      const rows = await apiService.getOfferMerchants(query);
      setMerchants(rows);
      if (rows.length === 0) {
        setSelectedMerchant(null);
        setEvents([]);
        return;
      }
      setSelectedMerchant((prev) => {
        if (prev && rows.some((row) => row.id === prev.id)) {
          return prev;
        }
        return null;
      });
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view merchant events.');
      } else {
        message.error('Failed to load merchants');
      }
    } finally {
      setLoadingMerchants(false);
    }
  }, [navigate]);

  const loadEventsForMerchant = useCallback(async (merchant: MerchantSummaryDTO) => {
    if (!merchant) {
      setEvents([]);
      return;
    }
    setLoadingEvents(true);
    try {
      const rows = await apiService.getOfferMerchantEvents(merchant.id, {
        periodMode: 'all_time',
      });
      setEvents(
        [...rows].sort(
          (a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime()
        )
      );
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view merchant events.');
      } else {
        message.error('Failed to load merchant events');
      }
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    loadMerchants('');
  }, [loadMerchants, navigate]);

  const merchantColumns = useMemo(() => ([
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: 'Name', dataIndex: 'name', key: 'name' }
  ]), []);

  const eventColumns = useMemo(() => ([
    { title: 'ID', dataIndex: 'agentId', key: 'agentId', width: 100 },
    { title: 'Name', dataIndex: 'agentName', key: 'agentName' },
    { title: 'Company', dataIndex: 'company', key: 'company' },
    {
      title: 'Date',
      dataIndex: 'dateTime',
      key: 'dateTime',
      render: (dateTime: string) => new Date(dateTime).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' })
    },
    { title: 'Amount AED', dataIndex: 'amountAed', key: 'amountAed', render: (value?: string) => value || '-' },
    { title: 'Rera Card', dataIndex: 'licenseId', key: 'licenseId' },
    {
      title: 'Event',
      dataIndex: 'eventTag',
      key: 'eventTag',
      render: (value: string) => <Tag color="blue">{value}</Tag>
    }
  ]), []);

  const handleExport = async (values: { periodMode: 'range' | 'all_time'; dateRange?: [dayjs.Dayjs, dayjs.Dayjs] }) => {
    setExportLoading(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;
      if (values.periodMode === 'range' && values.dateRange) {
        startDate = values.dateRange[0].format('YYYY-MM-DD');
        endDate = values.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await apiService.exportMerchantEvents({
        periodMode: values.periodMode,
        startDate,
        endDate,
        q: searchQuery.trim() || undefined
      });

      if (response.success && response.status === 'empty') {
        message.info('No merchant events found for selected filters and period');
      } else if (response.success && response.status === 'completed' && response.body) {
        const url = window.URL.createObjectURL(response.body);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.msg || 'merchant-events.zip';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('Merchant events exported successfully');
      } else {
        message.error(response.msg || 'Export failed');
      }
      setIsExportModalVisible(false);
      exportForm.resetFields();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to export merchant events.');
      } else {
        message.error('Failed to export merchant events');
      }
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <Search
          placeholder="Search merchants"
          allowClear
          enterButton={<SearchOutlined />}
          size="large"
          style={{ width: 380, maxWidth: '100%' }}
          onSearch={(value) => {
            setSearchQuery(value);
            loadMerchants(value);
          }}
        />
        <Space>
          <Button
            size="large"
            onClick={() => {
              setSearchQuery('');
              loadMerchants('');
            }}
          >
            Clear Search
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            onClick={() => setIsExportModalVisible(true)}
            loading={exportLoading}
          >
            Export
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        loading={loadingMerchants}
        columns={merchantColumns}
        dataSource={merchants}
        size="small"
        pagination={{ pageSize: 100 }}
        onRow={(record) => ({
          onClick: () => {
            setSelectedMerchant(record);
            setIsDetailsModalVisible(true);
            void loadEventsForMerchant(record);
          }
        })}
        rowClassName={(record) => record.id === selectedMerchant?.id ? 'ant-table-row-selected' : ''}
      />

      <Modal
        title={selectedMerchant ? `Merchant Details: ${selectedMerchant.name}` : 'Merchant Details'}
        open={isDetailsModalVisible}
        onCancel={() => {
          setIsDetailsModalVisible(false);
        }}
        width={1100}
        footer={[
          <Button key="close" onClick={() => setIsDetailsModalVisible(false)}>
            Close
          </Button>
        ]}
      >
        <Table
          rowKey={(row) => `${row.merchantId}-${row.agentId}-${row.dateTime}-${row.eventTag}`}
          loading={loadingEvents}
          columns={eventColumns}
          dataSource={events}
          locale={{ emptyText: 'No events found for selected merchant' }}
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      <Modal
        title="Export merchant events"
        open={isExportModalVisible}
        onCancel={() => {
          setIsExportModalVisible(false);
          exportForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsExportModalVisible(false);
            exportForm.resetFields();
          }}>
            Cancel
          </Button>,
          <Button key="export" type="primary" onClick={() => exportForm.submit()} loading={exportLoading}>
            Export
          </Button>
        ]}
      >
        <Form
          form={exportForm}
          layout="vertical"
          initialValues={{ periodMode: 'range' }}
          onFinish={handleExport}
        >
          <Form.Item name="periodMode" label="Period mode">
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              options={[
                { value: 'range', label: 'Date Range' },
                { value: 'all_time', label: 'All Time' }
              ]}
              onChange={(event) => {
                if (event.target.value === 'all_time') {
                  exportForm.setFieldsValue({ dateRange: undefined });
                }
              }}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.periodMode !== cur.periodMode}>
            {({ getFieldValue }) => getFieldValue('periodMode') === 'range' ? (
              <Form.Item
                name="dateRange"
                label="Date range"
                rules={[{ required: true, message: 'Please select date range' }]}
              >
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            ) : null}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OfferMerchants;
